import { useMemo } from "react"
import { useSelector } from "react-redux"
import { map, chain } from "lodash"
import useResource from "hooks/resources"
import { useAccountAlias } from "resources/account"
import { computeBaseHistory } from "helpers/base"
import { combineTanStackStatuses, OK, ResourceResult } from "helpers/data-statuses"
import { usePortfolios, Portfolio } from "resources/portfolios"
import { useBaskets, Basket } from "resources/baskets"
import { useExperiments, type Experiment } from "resources/experiments"
import { getRecentInstruments } from "selectors/recentInstruments"
import { IsoDate } from "helpers/types/dates"
import { ReduxState } from "model/state"

export const RECENT_INSTRUMENT_SEARCH_TYPES = {
  BASE: "base",
  GENERAL: "general",
  EXPERIMENT: "experiment",
  DASHBOARD: "dashboard",
  COMPARISON: "comparison",
  REBALANCE: "rebalance",
  FILTER: "filter",
} as const

export const INSTRUMENT_TYPES = {
  benchmark: "BENCHMARK",
  portfolio: "PORTFOLIO",
  index: "INDEX",
  researchTopic: "RESEARCH_TOPIC",
  etf: "ETF",
  basket: "SWAP",
} as const

export interface Index {
  id: string
  name: string
  description: string
  availableFrom: IsoDate
  terminationDate: IsoDate | null
}

export interface Benchmark {
  id: string
  name: string
  availableFrom: IsoDate
}

export interface ETF {
  id: string
  internalId: string
  description: string
  ticker: string
  mic: string
  adv: number
  availableFrom: IsoDate
  hasComposition: boolean
}

export interface ActiveInstruments {
  etfs: ETF[] | null
  portfolios: Portfolio[] | null
  baskets: Basket[] | null
  indices: Index[] | null
  benchmarks: Benchmark[] | null
  experiments?: Experiment[] | null
}

interface RecentInstrument {
  searchType: string
  order: number
  [key: string]: unknown
}

export function getRecentInstrumentsQueryUrl(account: string | null, modelId: string | null) {
  if (!modelId || !account) {
    return null
  }
  return {
    id: `recentInstruments-${modelId}`,
    url: `/api/${account}/recent-instruments?${new URLSearchParams({ modelId })}`,
  }
}

const INDEXES_QUERY = `
  query {
    indexes {
      id
      name
      description
      availableFrom
      terminationDate
    }
  }`

const BENCHMARK_GRAPHQL_QUERY = `query ($modelId: String!) {
  model (id: $modelId) {
    benchmarks {
      id
      name
      availableFrom
    }
  }
}`

export function useIndexes(): ResourceResult<Index[]> {
  return useResource<Index[]>({
    resource: "indexes",
    url: () => ({
      id: "indexes",
      query: INDEXES_QUERY,
    }),
  }, "indexes")
}

export function useBenchmarks(modelId: string | null): ResourceResult<Benchmark[]> {
  return useResource<Benchmark[]>({
    resource: "modelBenchmarks",
    url: () => {
      if (!modelId) {
        return null
      }
      return {
        id: `modelBenchmarks-${modelId}`,
        query: BENCHMARK_GRAPHQL_QUERY,
        variables: { modelId },
      }
    },
  }, ["model", "benchmarks"])
}

export function useETFs(modelId: string | null): ResourceResult<ETF[]> {
  const account = useAccountAlias()
  const [etfs, etfsStatus] = useResource<ETF[]>({
    resource: "etfs",
    url: () => modelId ? `/api/${account}/m/${modelId}/etfs` : null,
  }, "etfs")

  const etfsWithInternalId = useMemo(() => {
    return map(etfs, (etf) => ({
      ...etf,
      id: etf.internalId,
    }))
  }, [etfs])

  if (etfsStatus !== OK) {
    return [null, etfsStatus]
  }

  return [etfsWithInternalId, etfsStatus]
}

export function useActiveInstruments(modelId: string | null, shouldUseExperiments?: boolean): ResourceResult<ActiveInstruments> {
  const [etfs, etfsStatus] = useETFs(modelId)
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [baskets, basketsStatus] = useBaskets()
  const [indexes, indexesStatus] = useIndexes()
  const [benchmarks, benchmarksStatus] = useBenchmarks(modelId)
  const [experiments, experimentsStatus] = useExperiments(Boolean(shouldUseExperiments))

  const statuses = [etfsStatus, portfoliosStatus, basketsStatus, indexesStatus, benchmarksStatus]
  if (shouldUseExperiments) {
    statuses.push(experimentsStatus)
  }
  const status = combineTanStackStatuses(statuses)

  return useMemo(() => {
    let instruments: ActiveInstruments | null = null
    if (status === OK) {
      instruments = {
        etfs,
        portfolios,
        baskets,
        indices: indexes,
        benchmarks,
        ...shouldUseExperiments && { experiments },
      }
    }

    return [instruments, status] as ResourceResult<ActiveInstruments>
  }, [etfs, portfolios, baskets, indexes, benchmarks, status, experiments, shouldUseExperiments, experimentsStatus])
}

export function useRecentInstrumentBaseHistory(modelId: string | null): ResourceResult<unknown> {
  const recentUnifiedSearchInstruments = useSelector<ReduxState, RecentInstrument[]>(getRecentInstruments)
  const [activeInstruments, activeInstrumentsStatus] = useActiveInstruments(modelId)

  const recentHistoryBases = useMemo(
    () => {
      if (!recentUnifiedSearchInstruments) {
        return []
      }
      return chain(recentUnifiedSearchInstruments)
        .filter(instrument => instrument.searchType === RECENT_INSTRUMENT_SEARCH_TYPES.BASE)
        .orderBy("order")
        .value()
    },
    [recentUnifiedSearchInstruments],
  )

  const baseHistory = useMemo(
    () => computeBaseHistory(recentHistoryBases, null, activeInstruments),
    [recentHistoryBases, activeInstruments],
  )

  if (activeInstrumentsStatus !== OK) {
    return [null, activeInstrumentsStatus]
  }

  return [baseHistory, OK]
}

// Had to build these types earlier, leaving them here even if TS conversion is not done.
// interface AvailableBasket {
//   ticker: string // Ticker
//   description: string
//   availableFrom: IsoDate
//   terminationDate: IsoDate | null
//   // [TODO:TS]
//   currencyOverride: null
//   // [TODO:TS]
//   lastUpdated: null
// }

// interface AvailableBenchmark {
//   id: string
//   name: string
//   availableFrom: IsoDate
// }

// interface AvailableEtf {
//   id: string // internal id
//   internalId: string // internal id
//   description: string
//   ticker: string // Ticker
//   mic: string // MIC
//   adv: number
//   availableFrom: IsoDate
//   hasComposition: boolean
// }

// interface AvailableIndex {
//   id: string
//   name: string
//   description: string
//   availableFrom: IsoDate
//   terminationDate: IsoDate | null
// }

// interface AvailablePortfolio {
//   id: Uuid
//   name: string
//   alias: string
//   description: string
//   live: boolean
//   intradayEnabled: boolean
//   fxHedgeEnabled: boolean
//   shortId: string // [TODO] how is that different from alias?
//   modelId: string
//   // [TODO:TS] Type the following fields
//   betaSecurityId: null
//   betaResourceId: null
//   betaResourceType: null
//   betaBenchmarkId: null
//   betaIndexId: null
//   currencyOverride: null
//   lastUpdated: null
// }
